import { create } from 'zustand';
import type { SelectionMode } from '../types/designer';

// ============================================
// 选择状态管理
// ============================================

interface SelectionState {
  // 选择的节点ID列表
  selectedIds: string[];
  
  // 选择模式
  selectionMode: SelectionMode;
  
  // 上一次选择的ID（用于Shift多选）
  lastSelectedId: string | null;
  
  // 选择框状态
  isBoxSelecting: boolean;
  boxSelectStart: { x: number; y: number } | null;
  boxSelectEnd: { x: number; y: number } | null;

  /** 2026-10-15（P0-⑤ §8.2）：灯光选中（场景灯光 gizmo ↔ 树/面板联动） */
  lightSelectedId: string | null;
  selectLight: (id: string | null) => void;

  /** 2026-10-17（体积元素 S1）：体积选中（gizmo ↔ 面板联动） */
  volumeSelectedId: string | null;
  selectVolume: (id: string | null) => void;

  /** 2026-10-17（标绘层 S2）：标绘选中（手柄 ↔ 面板/树联动） */
  annotationSelectedId: string | null;
  selectAnnotation: (id: string | null) => void;

  /** S0-A：场景内 3D 图表实体选中（gizmo / 调试桥联动） */
  chart3dSelectedId: string | null;
  selectChart3D: (id: string | null) => void;
  
  // Actions
  select: (id: string, multi?: boolean) => void;
  deselect: (id: string) => void;
  toggleSelect: (id: string) => void;
  selectAll: (ids: string[]) => void;
  deselectAll: () => void;
  setSelectionMode: (mode: SelectionMode) => void;
  startBoxSelect: (point: { x: number; y: number }) => void;
  updateBoxSelect: (point: { x: number; y: number }) => void;
  endBoxSelect: () => void;
  isSelected: (id: string) => boolean;
  getSelectedCount: () => number;
}

export const useSelectionStore = create<SelectionState>((set, get) => ({
  selectedIds: [],
  selectionMode: 'single',
  lastSelectedId: null,
  isBoxSelecting: false,
  boxSelectStart: null,
  boxSelectEnd: null,
  lightSelectedId: null,
  volumeSelectedId: null,
  annotationSelectedId: null,
  chart3dSelectedId: null,

  selectLight: (id) => set({ lightSelectedId: id, ...(id ? { chart3dSelectedId: null } : {}) }),
  selectVolume: (id) => set({ volumeSelectedId: id, ...(id ? { chart3dSelectedId: null } : {}) }),
  selectAnnotation: (id) => set({ annotationSelectedId: id, ...(id ? { chart3dSelectedId: null } : {}) }),
  selectChart3D: (id) => set({
    chart3dSelectedId: id,
    ...(id ? {
      selectedIds: [],
      lastSelectedId: null,
      lightSelectedId: null,
      volumeSelectedId: null,
      annotationSelectedId: null,
    } : {}),
  }),
  
  select: (id: string, multi: boolean = false) => {
    set((state) => {
      if (multi) {
        // 多选模式：添加到现有选择
        if (state.selectedIds.includes(id)) {
          return state; // 已选中，不重复添加
        }
        return {
          selectedIds: [...state.selectedIds, id],
          lastSelectedId: id,
        };
      } else {
        // 单选模式：替换选择
        return {
          selectedIds: [id],
          lastSelectedId: id,
        };
      }
    });
  },
  
  deselect: (id: string) => {
    set((state) => ({
      selectedIds: state.selectedIds.filter(i => i !== id),
      lastSelectedId: state.lastSelectedId === id ? null : state.lastSelectedId,
    }));
  },
  
  toggleSelect: (id: string) => {
    const { selectedIds } = get();
    if (selectedIds.includes(id)) {
      get().deselect(id);
    } else {
      get().select(id, true);
    }
  },
  
  selectAll: (ids: string[]) => {
    set({ selectedIds: [...new Set(ids)] });
  },
  
  deselectAll: () => {
    set({ selectedIds: [], lastSelectedId: null, chart3dSelectedId: null });
  },
  
  setSelectionMode: (mode: SelectionMode) => {
    set({ selectionMode: mode });
  },
  
  startBoxSelect: (point: { x: number; y: number }) => {
    set({
      isBoxSelecting: true,
      boxSelectStart: point,
      boxSelectEnd: point,
    });
  },
  
  updateBoxSelect: (point: { x: number; y: number }) => {
    set({ boxSelectEnd: point });
  },
  
  endBoxSelect: () => {
    set({
      isBoxSelecting: false,
      boxSelectStart: null,
      boxSelectEnd: null,
    });
  },
  
  isSelected: (id: string) => {
    return get().selectedIds.includes(id);
  },
  
  getSelectedCount: () => {
    return get().selectedIds.length;
  },
}));

export default useSelectionStore;
